import { getInfluxdbData } from '../api/influxdb-tools';
import { VERALLIA_MONITORING_CONFIG } from '../../config/monitoring/verallia-monitoring-config';

const DANGER_STATES = new Set([1, 4, 5, 6]);
const WARN_STATES = new Set([2, 3, 7, 8, 9]);

const STATE_LABELS = {
  0: 'OK',
  1: 'Black',
  2: 'Low luminosity',
  3: 'Moving',
  4: 'Obstructed',
  5: 'Unreachable',
  6: 'Stream dead',
  7: 'Overexposed',
  8: 'Blurry',
  9: 'Uniform',
};

const toTimestamp = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;

  // Handle InfluxDB timestamps in seconds, milliseconds, microseconds or nanoseconds.
  const numeric = typeof value === 'number' ? value : typeof value === 'string' && /^-?\d+(?:\.\d+)?$/.test(value.trim()) ? Number(value) : null;
  if (numeric !== null) {
    if (!Number.isFinite(numeric)) return null;
    const magnitude = Math.abs(numeric);
    if (magnitude >= 1e17) return numeric / 1e6;
    if (magnitude >= 1e14) return numeric / 1e3;
    return magnitude < 1e11 ? numeric * 1000 : numeric;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '' || typeof value === 'string' && value.trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const toIso = (timestamp) => timestamp === null ? null : new Date(timestamp).toISOString();
const roundOneDecimal = (value) => Math.round((value + Number.EPSILON) * 10) / 10;
const utcStartOfDay = (now) => Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
const getDeviceId = (point) => point.ip || point.camera_id || point.device_id || null;

const getStateClass = (state) => {
  if (state === null) return 'none';
  if (DANGER_STATES.has(state)) return 'danger';
  if (WARN_STATES.has(state)) return 'warn';
  return state === 0 ? 'ok' : 'none';
};

const getWorstState = (states) => states.find((state) => DANGER_STATES.has(state))
  ?? states.find((state) => WARN_STATES.has(state))
  ?? (states.length > 0 ? 0 : null);

const normalizeInfluxPoints = (rows) => {
  if (!Array.isArray(rows)) throw new Error('Invalid InfluxDB response: an array was expected.');
  return rows.map((row) => ({
    time: toTimestamp(row._time ?? row.time),
    value: row._value ?? row.value,
    field: row._field ?? row.field,
    ip: row.ip,
    camera_id: row.camera_id,
    device_id: row.device_id,
    client_id: row.client_id,
    // Computed by Flux using InfluxDB time, independently of the browser clock.
    age_seconds: toNumber(row.monitoring_age_seconds),
  }));
};

export const buildMonitoringTimeline = (points, startTimestamp, endTimestamp, intervalMinutes) => {
  if (startTimestamp >= endTimestamp) return [];
  const intervalMs = intervalMinutes * 60 * 1000;
  if (!Number.isFinite(intervalMs) || intervalMs <= 0) throw new Error('Invalid monitoring timeline interval.');

  const segments = [];
  const sortedPoints = [...points].filter((point) => point.time !== null).sort((a, b) => a.time - b.time);
  let cursor = 0;

  for (let start = startTimestamp; start < endTimestamp; start += intervalMs) {
    const end = Math.min(start + intervalMs, endTimestamp);
    const states = [];

    while (cursor < sortedPoints.length && sortedPoints[cursor].time <= end) {
      if (sortedPoints[cursor].time > start) states.push(sortedPoints[cursor].state);
      cursor += 1;
    }

    const dominant = getWorstState(states);
    segments.push({
      start: toIso(start),
      end: toIso(end),
      state: getStateClass(dominant),
      dominant_state: dominant,
      dominant_label: dominant === null ? '' : STATE_LABELS[dominant] ?? '',
    });
  }

  return segments;
};

export const buildVeralliaMonitoringStatus = (data, now = new Date(), config = VERALLIA_MONITORING_CONFIG) => {
  const nowTimestamp = now.getTime();
  const dayStart = utcStartOfDay(now);
  const staleMs = config.staleAfterSeconds * 1000;
  const aiConfig = config.aiMonitoring;
  const aiEnabled = !!aiConfig?.enabled;

  const statePoints = [...data.states].map((point) => {
    const state = toNumber(point.value);
    return { ...point, state };
  }).filter((point) => Number.isInteger(point.state) && point.state !== 6 && point.time !== null);

  const pointsByDevice = new Map();
  statePoints.forEach((point) => {
    const id = getDeviceId(point);
    if (!id) return;
    if (!pointsByDevice.has(id)) pointsByDevice.set(id, []);
    pointsByDevice.get(id).push(point);
  });
  pointsByDevice.forEach((points) => points.sort((a, b) => a.time - b.time));

  const lastReachableByDevice = new Map();
  data.reachable.forEach((point) => {
    const id = getDeviceId(point);
    if (!id || point.time === null) return;
    const previous = lastReachableByDevice.get(id);
    if (previous === undefined || point.time > previous) lastReachableByDevice.set(id, point.time);
  });

  const lastHeartbeat = data.heartbeat.reduce((latest, point) => point.time !== null && (latest === null || point.time > latest) ? point.time : latest, null);
  const service = { alive: data.heartbeat.length > 0, last_heartbeat: toIso(lastHeartbeat) };

  const aiByDevice = new Map();
  if (aiEnabled) {
    data.ai.forEach((point) => {
      const id = String(point.camera_id ?? '').trim();
      if (!id) return;

      // Prefer the point age calculated by InfluxDB, avoiding browser clock offsets.
      // Fall back to the timestamp for compatibility with older query results.
      const serverAge = toNumber(point.age_seconds);
      const pointTime = point.time ?? (serverAge !== null ? nowTimestamp - serverAge * 1000 : null);
      if (pointTime === null) return;

      const previous = aiByDevice.get(id);
      if (!previous || pointTime > previous.last_run) {
        const ageSeconds = serverAge ?? (nowTimestamp - pointTime) / 1000;
        aiByDevice.set(id, {
          is_running: toNumber(point.value) === 1 && ageSeconds >= 0 && ageSeconds <= aiConfig.staleAfterSeconds,
          last_run: pointTime,
        });
      }
    });
  }

  const devices = config.devices.map((device) => {
    const points = pointsByDevice.get(device.id) ?? [];
    const lastPoint = points.length > 0 ? points[points.length - 1] : null;
    const lastChecked = lastPoint?.time ?? null;
    const checkStale = lastChecked === null || nowTimestamp - lastChecked > staleMs;
    const currentState = lastPoint && !checkStale ? lastPoint.state : null;
    const lastSeen = lastReachableByDevice.get(device.id) ?? null;
    const aiMonitored = aiEnabled && !!device.aiMonitoring;
    const aiStatus = aiByDevice.get(device.id);

    return {
      id: device.id,
      name: device.name ?? device.id,
      state: currentState,
      state_label: currentState === null ? 'No data' : STATE_LABELS[currentState] ?? 'Unknown',
      state_class: getStateClass(currentState),
      last_seen: toIso(lastSeen),
      stale: lastSeen === null || nowTimestamp - lastSeen > staleMs,
      timeline: buildMonitoringTimeline(points, dayStart, nowTimestamp, config.timelineBucketMinutes),
      ai_monitored: aiMonitored,
      ai_running: aiMonitored ? aiStatus?.is_running ?? false : null,
      ai_last_run: aiMonitored ? toIso(aiStatus?.last_run ?? null) : null,
    };
  });

  const diskFields = {};
  let diskLastSeen = null;
  data.disk.forEach((point) => {
    if (['used_prc', 'total_gb', 'used_gb', 'available_gb'].includes(point.field)) {
      diskFields[point.field] = point.value;
      if (point.time !== null && (diskLastSeen === null || point.time > diskLastSeen)) diskLastSeen = point.time;
    }
  });

  const usedRatio = toNumber(diskFields.used_prc);
  const usedGb = toNumber(diskFields.used_gb);
  const totalGb = toNumber(diskFields.total_gb);
  const availableGb = toNumber(diskFields.available_gb);
  const hasDiskData = [usedRatio, usedGb, totalGb, availableGb].every((value) => value !== null);
  const usedPercent = hasDiskData ? roundOneDecimal(usedRatio * 100) : null;
  const storage = {
    used_percent: usedPercent,
    used_gb: hasDiskData ? roundOneDecimal(usedGb) : null,
    free_gb: hasDiskData ? roundOneDecimal(availableGb) : null,
    total_gb: hasDiskData ? totalGb : null,
    state: !hasDiskData ? 'none' : usedPercent >= 95 ? 'danger' : usedPercent >= 85 ? 'warn' : 'ok',
    last_seen: toIso(diskLastSeen),
  };

  return { service, storage, devices, generated_at: now.toISOString() };
};

const buildQueries = (now, config) => {
  const fromBucket = `from(bucket: "${config.bucket}")`;
  const clientFilter = `|> filter(fn: (r) => r.client_id == "${config.clientId}")`;
  const stateFilter = '|> filter(fn: (r) => (r._measurement == "camera_state" or r._measurement == "device_state") and r._field == "state")';

  return {
    states: `${fromBucket}\n  |> range(start: ${new Date(utcStartOfDay(now)).toISOString()})\n  ${stateFilter}\n  ${clientFilter}`,
    reachable: `${fromBucket}\n  |> range(start: ${config.lastSeenLookback})\n  ${stateFilter}\n  ${clientFilter}\n  |> filter(fn: (r) => r._value != 5)\n  |> last()`,
    heartbeat: `${fromBucket}\n  |> range(start: -${config.staleAfterSeconds}s)\n  |> filter(fn: (r) => r._measurement == "service_heartbeat")\n  ${clientFilter}\n  |> last()`,
    disk: `${fromBucket}\n  |> range(start: ${config.diskLookback})\n  |> filter(fn: (r) => r._measurement == "space_monitoring")\n  ${clientFilter}\n  |> filter(fn: (r) => contains(value: r._field, set: ["used_prc", "total_gb", "used_gb", "available_gb"]))\n  |> last()`,
    ai: `from(bucket: "${config.aiMonitoring.bucket}")\n  |> range(start: ${config.aiMonitoring.lastRunLookback})\n  |> filter(fn: (r) => r._measurement == "${config.aiMonitoring.measurement}" and r._field == "${config.aiMonitoring.field}")\n  |> filter(fn: (r) => exists r.camera_id)\n  |> last()\n  |> group(columns: ["camera_id"])\n  |> sort(columns: ["_time"])\n  |> last()\n  |> map(fn: (r) => ({ r with monitoring_age_seconds: float(v: int(v: now()) - int(v: r._time)) / 1000000000.0 }))\n  |> keep(columns: ["camera_id", "_time", "_value", "monitoring_age_seconds"])`,
  };
};

export const getVeralliaMonitoringQueries = (now = new Date(), config = VERALLIA_MONITORING_CONFIG) => buildQueries(now, config);

export const getVeralliaMonitoringStatus = async (config = VERALLIA_MONITORING_CONFIG) => {
  const now = new Date();
  const queries = buildQueries(now, config);
  const needsAi = config.aiMonitoring?.enabled && config.devices.some((device) => device.aiMonitoring);
  const keys = needsAi ? ['states', 'reachable', 'heartbeat', 'disk', 'ai'] : ['states', 'reachable', 'heartbeat', 'disk'];

  const results = await Promise.all(keys.map(async (key) => {
    const rows = await getInfluxdbData(null, null, [], [], null, null, null, null, null, [], null, queries[key]);
    return [key, normalizeInfluxPoints(rows)];
  }));

  return buildVeralliaMonitoringStatus({ states: [], reachable: [], heartbeat: [], disk: [], ai: [], ...Object.fromEntries(results) }, new Date(), config);
};
